import { useMemo } from 'react';

import {
  roundNumber,
  getMaxQuantityLimit,
  filterRemovedProducts,
  mapPriceExistsInBundle,
  getBundlePriceCalculations,
  combineBundleProducts,
  getBundlePriceTotals,
  hasMapSingleStrikethroughItem,
  bundleHasEffectiveMapItem,
  bundleHasItemLevelMapHeadroom
} from '../utils/product-bundle-utils';
import { extractBundleAdjustments } from '../utils/bundle-pricing-helpers';
import { getBundlePricing } from '../utils/productBundleData';

export const useBundlePricingDetails = ({
  product, products, loading: productsLoading, isCustomerIdentified = false, selectedAccessories = [],
  isBundleModified = false
}) => {
  const {
    priceUnitOfMeasure, showQuantityLimit
  } = product?.features ?? {};

  // The parent `product` query isn't re-fetched on change, so its rolled-up adjustments go stale once
  // modified. Use them only on pristine load; after any change rely on the fresh per-item adjustments.
  const parentBundleAdjustments = isBundleModified ? null : product?.pricing?.bundlePromotionalAdjustments;
  const parentPromotion = product?.pricing?.promotion;
  const parentSpecialBuy = product?.pricing?.specialBuy;

  const activeProducts = useMemo(
    () => filterRemovedProducts(combineBundleProducts(products, selectedAccessories)),
    [products, selectedAccessories]
  );
  const mapDetail = useMemo(
    () => mapPriceExistsInBundle(activeProducts, isCustomerIdentified),
    [activeProducts, isCustomerIdentified]
  );

  // MAP bundle with no item priced below its effectiveMap by an item-level discount: subtracting
  // bundle promos (EXCHANGE/BMSM) would collapse the advertised price to the cart price, so suppress them.
  const suppressBundlePromos = useMemo(
    () => bundleHasEffectiveMapItem(activeProducts) && !bundleHasItemLevelMapHeadroom(activeProducts),
    [activeProducts]
  );

  const adjustments = useMemo(
    () => (activeProducts?.length
      ? extractBundleAdjustments(activeProducts, parentBundleAdjustments, suppressBundlePromos)
      : null),
    [activeProducts, parentBundleAdjustments, suppressBundlePromos]
  );
  const maxQuantityLimit = useMemo(() => getMaxQuantityLimit(activeProducts), [activeProducts]);
  const priceTotals = useMemo(() => getBundlePriceTotals(activeProducts), [activeProducts]);
  const hasMapSingleStrikethrough = useMemo(
    () => hasMapSingleStrikethroughItem(activeProducts),
    [activeProducts]
  );

  const calculations = useMemo(
    () => (adjustments
      ? getBundlePriceCalculations({
        ...adjustments,
        firstPriceTotal: priceTotals?.firstPriceTotal,
        secondPriceTotal: priceTotals?.secondPriceTotal,
        valuePriceTotal: priceTotals?.valuePriceTotal
      })
      : null),
    [adjustments, priceTotals]
  );

  const loading = !activeProducts?.length || productsLoading;

  const pricing = useMemo(
    () => (loading ? null : getBundlePricing(
      {
        originalPrice: calculations?.regularPrice,
        discount: calculations?.totalDollarOff,
        percent: calculations?.totalPercentOff,
        specialPrice: roundNumber({ number: calculations?.finalDiscountedPrice, decimal: 2 }),
        realFinalPrice: roundNumber({ number: calculations?.realFinalPrice, decimal: 2 }),
        mapDetail,
        quantityLimit: showQuantityLimit ? maxQuantityLimit?.quantityLimit : null,
        priceUnitOfMeasure,
        hasMapSingleStrikethrough,
        parentPromotion,
        parentSpecialBuy
      }
    )),
    [
      loading, calculations, mapDetail, showQuantityLimit, maxQuantityLimit, priceUnitOfMeasure,
      hasMapSingleStrikethrough, parentPromotion, parentSpecialBuy
    ]
  );

  return {
    loading,
    pricing,
    adjustments,
    mapDetail,
    calculations,
    maxQuantityLimit
  };
};
